import { RootState } from 'store/store.types';
import { createSelector } from '@reduxjs/toolkit';
import {
    mapActivityContentForDetail,
    mapActivityDetail,
    mapActivityForDetail,
    mapActivityLocation,
    mapActivityRepresentativeInfo,
    mapDescription,
    mapItineraryByDetail,
} from 'gql/service/graph/activityService';
import { DetailsState } from 'component/page/detail/store/details';
import { NavigationHashEnum } from 'core/type/Details.types';
import { PageContentStatus } from 'core/type/PageContentStatus';
import { isDefaultGqlState } from 'store/api/activitiesGqlApi';
import { selectBrowserInfoFromBootstrap, selectMobileDeviceTypeFromBootstrap, selectWebViewFeatureToggles } from 'store/slice/bootstrap';
import { AppFeaturesToggleEnum, CartWebViewBridgeEnum, MobileDeviceType } from 'core/type/BootstrapConfig.types';
import { checkIsHookFunctionExist } from 'core/app/appService';
import { SectionType } from 'gql/autogen/SectionType';

export const selectDetailState = (state: RootState) => state.details;
export const selectReviewState = (state: RootState) => state.review;
export const selectIsScrolling = createSelector(selectDetailState, (state) => state.isScrolling || false);
export const selectResultFromDetailState = createSelector(selectDetailState, (state) => (state && state.detailsResponse && state.detailsResponse.result) || {});
export const selectExpandedAccordionFromDetailState = createSelector(selectDetailState, (state) => (state && state.expandedAccordion) || {});
export const selectExpandedModularAccordionFromDetailState = createSelector(selectDetailState, (state) => (state && state.expandedModularAccordion) || {});
export const selectSelectedTabFromDetailState = createSelector(selectDetailState, (state) => (state && state.selectedTab) || NavigationHashEnum.OverviewSection);
export const selectActivityTokenFromDetailState = createSelector(selectDetailState, (state) => state.activityToken || '');
export const selectReviewResultList = createSelector(selectReviewState, (state) => state.reviewResponse?.result?.reviews);
export const selectReviewResultPagination = createSelector(selectReviewState, (state) => state.reviewResponse?.result?.resultInfo?.pagination);
export const selectReviewSortAndFilterMatrix = createSelector(selectReviewState, (state) => state.reviewResponse?.result?.matrix);
export const selectCurrentReviewSort = createSelector(selectReviewState, (state) => state.currentSort);
export const selectCurrentReviewFilter = createSelector(selectReviewState, (state) => state.currentFilter);
export const selectReviewResultTotalFilteredReviews = createSelector(selectReviewState, (state) => state.reviewResponse?.result?.resultInfo?.totalFilteredReviews);
export const selectReviewTranslationState = createSelector(selectDetailState, (state) => state.reviewTranslationState);
export const selectActivityFromDetailState = createSelector(selectDetailState, mapActivityForDetail);
export const selectActivityContentDetailState = createSelector(selectDetailState, mapActivityContentForDetail);
export const selectActivityImagesFromDetailState = createSelector(selectActivityContentDetailState, (content) => (content && content.images) || []);
export const selectActivityFromContentDetailState = createSelector(selectActivityContentDetailState, (content) => content.activity);
export const selectActivityLocationFromContentDetailState = createSelector(selectActivityContentDetailState, (content) => content.activity?.location);
export const selectActivityContentLocale = createSelector(selectActivityContentDetailState, (content) => content.contentLocale || 'en');
export const selectActivityHighlightsFromContentDetailState = createSelector(selectActivityContentDetailState, (content) => content.activityHighlights || []);
export const selectActivityDetail = createSelector(selectDetailState, mapActivityDetail);
export const selectActivityOfferDetails = createSelector(selectActivityDetail, (state) => state.offerDetails || []);
export const selectActivityItinerary = createSelector(selectActivityDetail, mapItineraryByDetail);
export const selectActivityDescription = createSelector(selectActivityItinerary, (itinerary) => (itinerary && mapDescription(itinerary.description)) || undefined);
export const selectActivityLocations = createSelector(selectDetailState, mapActivityLocation);
export const selectActivityRepresentativeInfo = createSelector(selectActivityFromDetailState, mapActivityRepresentativeInfo);
export const selectFieldContentTypesFromContentDetailState = createSelector(selectResultFromDetailState, (state) => state && state.fieldContentTypes);
export const selectPageContentStatus = createSelector(selectDetailState, (state: DetailsState): PageContentStatus => {
    if (state.isLoading || isDefaultGqlState(state)) {
        return PageContentStatus.Loading;
    }

    if (state.error?.errorCode) {
        return PageContentStatus.SupplyError;
    }
    if (state.isError) {
        return PageContentStatus.StandardError;
    }
    return PageContentStatus.Success;
});

export const selectIntroductionFromContentDetailGenericSectionState = createSelector(selectActivityContentDetailState, (content) =>
    content.detail?.genericSection?.find((gc) => gc.sectionType === SectionType.INTRODUCTION),
);

export const selectExtraFeeFromContentDetailGenericSectionState = createSelector(selectActivityContentDetailState, (content) =>
    content.detail?.genericSection?.find((gc) => gc.sectionType === SectionType.SURCHARGE),
);

export const selectExtraFeeContentFromExtraFeeState = createSelector(
    selectExtraFeeFromContentDetailGenericSectionState,
    (genericSection) => genericSection && genericSection.content && genericSection.content,
);

const selectPurchaseNoticeFromGenericSectionState = createSelector(selectActivityContentDetailState, (content) =>
    content.detail?.genericSection?.find((gc) => gc.sectionType === SectionType.PURCHASE_NOTICE),
);
export const selectPurchaseNoticeContent = createSelector(selectPurchaseNoticeFromGenericSectionState, (genericSection) => (genericSection && genericSection.content) || []);

export const selectSuggestedRouteFromGenericSectionState = createSelector(selectActivityContentDetailState, (content) =>
    content.detail?.genericSection?.find((gc) => gc.sectionType === SectionType.SUGGESTED_ROUTE),
);
export const selectSuggestedRouteContent = createSelector(selectSuggestedRouteFromGenericSectionState, (genericSection) => (genericSection && genericSection.content) || []);

const selectGraphicFromGenericSectionState = createSelector(selectActivityContentDetailState, (content) =>
    content.detail?.genericSection?.find((gc) => gc.sectionType === SectionType.GRAPHIC),
);
export const selectGraphicContentFromGraphicState = createSelector(selectGraphicFromGenericSectionState, (genericSection) => (genericSection && genericSection.content) || []);

const selectSimCardFromGenericSectionState = createSelector(selectActivityContentDetailState, (content) =>
    content.detail?.genericSection?.find((gc) => gc.sectionType === SectionType.SIM_CARD),
);
export const selectSimCardContent = createSelector(selectSimCardFromGenericSectionState, (genericSection) => (genericSection && genericSection.content) || []);

export const selectIsCartMigrationEnabled = createSelector(
    selectBrowserInfoFromBootstrap,
    selectMobileDeviceTypeFromBootstrap,
    selectWebViewFeatureToggles,
    (browserInfo, mobileDeviceType, webViewFeatureToggles) => {
        const isWebView = browserInfo.isWebView;
        const isHookFunctionExist = checkIsHookFunctionExist(mobileDeviceType, CartWebViewBridgeEnum.openCartBFWebView);
        const isIOSEnabled = mobileDeviceType === MobileDeviceType.iOS;
        const isAndroidEnabled = mobileDeviceType === MobileDeviceType.Android && webViewFeatureToggles?.includes(AppFeaturesToggleEnum.EnableOpenCartBFWebViewHook);
        return !isWebView || (isWebView && isHookFunctionExist && (isIOSEnabled || isAndroidEnabled));
    },
);

export const selectShouldSendCartMigrationFallbackMeasurement = createSelector(
    selectBrowserInfoFromBootstrap,
    selectMobileDeviceTypeFromBootstrap,
    (browserInfo, mobileDeviceType) => {
        const isWebView = browserInfo.isWebView;
        const isHookFunctionExist = checkIsHookFunctionExist(mobileDeviceType, CartWebViewBridgeEnum.openCartBFWebView);
        const isIOSEnabled = mobileDeviceType === MobileDeviceType.iOS;
        const isAndroidEnabled = mobileDeviceType === MobileDeviceType.Android;
        return isWebView && (isIOSEnabled || isAndroidEnabled) && !isHookFunctionExist;
    },
);

export const selectAboutActivityContent = createSelector(selectActivityContentDetailState, (content) => content.detail?.aboutActivity || '');

export const selectIsExpandFirstOfferCardInitiated = createSelector(selectDetailState, (state: DetailsState): boolean => {
    return state.isExpandFirstOfferCardInitiated ?? true;
});

export const selectCategoryIdsFromDetailState = createSelector(selectActivityContentDetailState, (content) => content.activity?.categories);
